import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import { dateOnlyFieldToLocalMidnight } from '../utils/timezone';

interface RentalWindow {
  _id: string;
  name?: string;
  windowType: 'monthly' | 'weekly' | 'daily';
  pricePerInterval: number;
  quantity: number;
  startTime?: string;
  endTime?: string;
  repeatDaysOfWeek?: number[];
  specificDate?: Date;
  isRepeating: boolean;
  startDayOfMonth?: number;
  startDayOfWeek?: number;
}

interface Listing {
  _id: string;
  name: string;
  title: string;
}

const ListingCalendarPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [windows, setWindows] = useState<RentalWindow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [listingRes, windowsRes] = await Promise.all([
        api.get<{ listing: Listing }>(`/listings/${id}`),
        api.get<{ windows: RentalWindow[] }>(`/windows/listing/${id}`),
      ]);
      setListing(listingRes.listing);
      setWindows(windowsRes.windows);
    } catch {
      toast.error('Failed to load calendar');
      navigate('/my-listings');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Separate windows by type
  const monthlyWindows = windows.filter(w => w.windowType === 'monthly');
  const weeklyWindows = windows.filter(w => w.windowType === 'weekly');
  const dailyWindows = windows.filter(w => w.windowType === 'daily');

  // Generate calendar days for current month + next 30 days
  const generateCalendarDays = () => {
    const days: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  // Check if a daily window is available on a given date
  const isDateAvailable = (date: Date, window: RentalWindow): boolean => {
    if (!window.isRepeating && window.specificDate) {
      const specificDate = dateOnlyFieldToLocalMidnight(window.specificDate);
      return specificDate.getTime() === date.getTime();
    }

    if (window.isRepeating && window.repeatDaysOfWeek) {
      const dayOfWeek = date.getDay();
      return window.repeatDaysOfWeek.includes(dayOfWeek);
    }

    return true;
  };

  // Get available time slots for a selected date
  const getTimeSlotsForDate = (date: Date): RentalWindow[] => {
    return dailyWindows.filter(window => isDateAvailable(date, window));
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
  };

  const formatDayName = (dayNumber: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNumber];
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!listing) {
    return <div className="text-center py-8">Listing not found</div>;
  }

  const timeSlots = selectedDate ? getTimeSlotsForDate(selectedDate) : [];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/my-listings')}
          className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
        >
          ← Back to My Listings
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          Calendar: {listing.title}
        </h1>
      </div>

      {/* Monthly Windows */}
      {monthlyWindows.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Monthly Rentals</h2>
          <div className="space-y-3">
            {monthlyWindows.map((window) => (
              <div
                key={window._id}
                className="border border-gray-300 rounded-lg p-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    {window.name && (
                      <h3 className="font-semibold text-gray-900 mb-1">{window.name}</h3>
                    )}
                    <p className="text-sm text-gray-600">
                      Starting day: {window.startDayOfMonth || 1} of each month
                    </p>
                    <p className="text-sm text-gray-600">
                      Available quantity: {window.quantity}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary-600">
                      ${window.pricePerInterval}
                    </p>
                    <p className="text-sm text-gray-500">per month</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Windows */}
      {weeklyWindows.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Weekly Rentals</h2>
          <div className="space-y-3">
            {weeklyWindows.map((window) => (
              <div
                key={window._id}
                className="border border-gray-300 rounded-lg p-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    {window.name && (
                      <h3 className="font-semibold text-gray-900 mb-1">{window.name}</h3>
                    )}
                    <p className="text-sm text-gray-600">
                      Starting day: {formatDayName(window.startDayOfWeek || 0)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Available quantity: {window.quantity}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary-600">
                      ${window.pricePerInterval}
                    </p>
                    <p className="text-sm text-gray-500">per week</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily Windows Calendar */}
      {dailyWindows.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Daily Rentals - Next 30 Days</h2>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2 mb-6">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-semibold text-gray-700 py-2">
                {day}
              </div>
            ))}
            
            {calendarDays.map((date, index) => {
              const hasAvailability = dailyWindows.some(window => isDateAvailable(date, window));
              const isSelected = selectedDate && selectedDate.getTime() === date.getTime();
              const isToday = new Date().toDateString() === date.toDateString();
              
              return (
                <button
                  key={index}
                  onClick={() => handleDateClick(date)}
                  className={`
                    p-2 rounded-lg border transition-colors
                    ${hasAvailability ? 'border-green-300 bg-green-50 hover:bg-green-100' : 'border-gray-200 bg-gray-50'}
                    ${isSelected ? 'ring-2 ring-primary-500 bg-primary-100' : ''}
                    ${isToday ? 'font-bold' : ''}
                  `}
                >
                  <div className="text-sm">
                    {date.getDate()}
                  </div>
                  {hasAvailability && (
                    <div className="text-xs text-green-600 mt-1">
                      ✓
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Time Slots for Selected Date */}
          {selectedDate && (
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Available Times for {selectedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </h3>
              {timeSlots.length === 0 ? (
                <p className="text-gray-600">No availability on this date</p>
              ) : (
                <div className="space-y-2">
                  {timeSlots.map((window) => (
                    <div
                      key={window._id}
                      className="border border-gray-300 rounded-lg p-3 flex justify-between items-center"
                    >
                      <div>
                        {window.name && (
                          <p className="font-semibold text-gray-900">{window.name}</p>
                        )}
                        <p className="text-sm text-gray-600">
                          {window.startTime} - {window.endTime}
                        </p>
                        <p className="text-sm text-gray-600">
                          Available: {window.quantity}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-primary-600">
                          ${window.pricePerInterval}
                        </p>
                        <p className="text-xs text-gray-500">per slot</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {monthlyWindows.length === 0 && weeklyWindows.length === 0 && dailyWindows.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600">No rental windows configured yet</p>
          <Button
            onClick={() => navigate(`/listings/${id}/windows`)}
            className="mt-4"
          >
            Add Rental Windows
          </Button>
        </div>
      )}
    </div>
  );
};

export default ListingCalendarPage;

